import Image from 'next/image'
import Link from 'next/link'

import { Tag } from '@/shared/components/Tag/Tag'
import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, Typography } from '@mui/material'

import type { Author } from '@/slices/blog/mocks/authors'

interface AboutAuthorProps {
  author: Author
}

export default function AboutAuthor({ author }: AboutAuthorProps) {
  return (
    <AboutAuthorSection>
      <SectionTitle variant="h5">About the Author</SectionTitle>

      <AuthorCard>
        <Link href={`/author/${author.id}`} passHref legacyBehavior>
          <AuthorLink>
            <AvatarWrapper>
              <Avatar
                src={author.avatar}
                alt={author.name}
                width={80}
                height={80}
              />
            </AvatarWrapper>

            <AuthorInfo>
              <AuthorName variant="h6">{author.name}</AuthorName>
              <AuthorTitle variant="body2">{author.title}</AuthorTitle>
              {author.credentials && (
                <Credentials variant="caption">{author.credentials}</Credentials>
              )}
              <AuthorBio variant="body2">{author.bio}</AuthorBio>

              {author.expertise && author.expertise.length > 0 && (
                <ExpertiseChips>
                  {author.expertise.slice(0, 3).map((skill) => (
                    <Tag key={skill} label={skill} size="small" type="glass-blur" glassEffect />
                  ))}
                </ExpertiseChips>
              )}

              <ViewProfileLink>View Profile →</ViewProfileLink>
            </AuthorInfo>
          </AuthorLink>
        </Link>
      </AuthorCard>
    </AboutAuthorSection>
  )
}

const AboutAuthorSection = styled('section')(({ theme }) => ({
  marginTop: pxToRem(60),
  marginBottom: pxToRem(40),
  paddingTop: pxToRem(40),
  borderTop: `2px solid ${theme.palette.divider}`,

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(80),
    paddingTop: pxToRem(60),
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(24),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: pxToRem(24),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(28),
    marginBottom: pxToRem(32),
  },
}))

const AuthorCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: pxToRem(16),
  padding: pxToRem(24),
  transition: 'all 0.3s ease',

  [theme.breakpoints.up('md')]: {
    padding: pxToRem(32),
  },
}))

const AuthorLink = styled('a')({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(20),
  textDecoration: 'none',
  color: 'inherit',

  '@media (min-width: 600px)': {
    flexDirection: 'row',
    gap: pxToRem(24),
  },
})

const AvatarWrapper = styled(Box)({
  flexShrink: 0,
  alignSelf: 'center',

  '@media (min-width: 600px)': {
    alignSelf: 'flex-start',
  },
})

const Avatar = styled(Image)(( ) => ({
  borderRadius: '50%',
  objectFit: 'cover',
}))

const AuthorInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(12),
  textAlign: 'center',

  '@media (min-width: 600px)': {
    textAlign: 'left',
  },
})

const AuthorName = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(22),
  fontWeight: 700,
  color: theme.palette.primary.contrastText,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
  },
}))

const AuthorTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(15),
  fontWeight: 500,
  color: theme.palette.primary.contrastText,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))

const Credentials = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(13),
  fontStyle: 'italic',
  color: theme.palette.primary.contrastText,
  opacity: 0.85,
}))

const AuthorBio = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  lineHeight: 1.7,
  color: theme.palette.primary.contrastText,
  opacity: 0.9,

  marginTop: pxToRem(4),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))

const ExpertiseChips = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: pxToRem(8),
  marginTop: pxToRem(8),
  justifyContent: 'center',

  '@media (min-width: 600px)': {
    justifyContent: 'flex-start',
  },
})

const ViewProfileLink = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 600,
  color: theme.palette.primary.contrastText,
  marginTop: pxToRem(8),
  transition: 'opacity 0.2s ease',

  '&:hover': {
    opacity: 0.8,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))
